import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router';
import z from 'zod';

import { useGetPackage, useGetPackageSuggestedPrompts } from '@/api/gen/knowledge-base';
import { TLConversationType, TLKbEmbeddingStatus, TLKbPackageStage } from '@/api/gen/tessera-backend-api.schemas';
import { type DisplayMode, FloatingChatShell } from '@/components/features/feature-chat/FloatingChatShell';
import {
  KB_LIST_EMPTY_STATE_SUBTITLE,
  KB_LIST_PROMPT_TEMPLATES,
  KB_PACKAGE_EMPTY_STATE_SUBTITLE,
  KB_PACKAGE_PROMPT_TEMPLATES,
  KB_SMOKE_TEST_PROMPT_TEMPLATE,
  kbContentPromptTemplates,
} from '@/components/features/knowledge-base/constants';
import { KbChatScopeBanner } from '@/components/features/knowledge-base/KbChatScopeBanner';
import {
  KbChatLauncherProvider,
  useKbChatLauncher,
} from '@/components/features/knowledge-base/store/kb-chat-launcher-store';

export const Route = createFileRoute('/_protected/t/$tenantId/knowledge-base')({
  validateSearch: z.object({
    chatConversationId: z.string().optional().catch(undefined),
  }),
  component: KnowledgeBaseLayout,
});

const KB_CHAT_NOT_READY_TITLE = 'Chat is available once document validation completes for this package.';

function KnowledgeBaseLayout() {
  return (
    <KbChatLauncherProvider>
      <KnowledgeBaseLayoutContent />
    </KbChatLauncherProvider>
  );
}

function KnowledgeBaseLayoutContent() {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('floating');
  const [chatMinimized, setChatMinimized] = useState(true);
  const { tenantId, packageId } = useParams({ strict: false });
  const { chatConversationId } = Route.useSearch();
  const pendingPrompt = useKbChatLauncher((s) => s.pendingPrompt);
  const consumePrompt = useKbChatLauncher((s) => s.consumePrompt);

  // Shares the detail page's query cache (which polls while validation runs), so the
  // launcher enables live when validation completes.
  const { data: pkg } = useGetPackage({ packageId: packageId ?? '' }, { query: { enabled: Boolean(packageId) } });
  const chatReady =
    !packageId ||
    pkg?.stage === TLKbPackageStage.EMBEDDED ||
    pkg?.embedding_status === TLKbEmbeddingStatus.EMBEDDING_COMPLETE;

  useEffect(() => {
    if (pendingPrompt && chatReady) setChatMinimized(false);
  }, [pendingPrompt, chatReady]);

  useEffect(() => {
    if (!chatReady) setChatMinimized(true);
  }, [chatReady]);

  // Content-aware prompts exist only after embedding (they come from wiki page titles
  // and validated documents), so fetch once the chat is usable; static templates cover
  // the list scope and any package without content-derived prompts yet.
  const { data: suggested } = useGetPackageSuggestedPrompts(
    { packageId: packageId ?? '' },
    { query: { enabled: Boolean(packageId) && chatReady, staleTime: 5 * 60_000 } },
  );
  const promptTemplates = useMemo(() => {
    if (!packageId) return KB_LIST_PROMPT_TEMPLATES;
    if (!suggested?.prompts.length) return KB_PACKAGE_PROMPT_TEMPLATES;
    return [...kbContentPromptTemplates(suggested.prompts), KB_SMOKE_TEST_PROMPT_TEMPLATE];
  }, [packageId, suggested]);

  return (
    <div className="flex size-full">
      <div className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
      <FloatingChatShell
        key={packageId ?? 'list'}
        chatType={TLConversationType.KB_CHAT}
        kbPackageId={packageId}
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        minimized={chatMinimized}
        onMinimizedChange={setChatMinimized}
        headerBanner={<KbChatScopeBanner packageId={packageId} />}
        emptyStateSubtitle={packageId ? KB_PACKAGE_EMPTY_STATE_SUBTITLE : KB_LIST_EMPTY_STATE_SUBTITLE}
        promptTemplates={promptTemplates}
        initialConversationId={chatConversationId}
        persistenceKey={`kb:${tenantId ?? ''}:${packageId ?? 'list'}`}
        pendingDraft={pendingPrompt ?? undefined}
        onPendingDraftConsumed={consumePrompt}
        launcherDisabled={!chatReady}
        launcherDisabledTitle={KB_CHAT_NOT_READY_TITLE}
      />
    </div>
  );
}
